import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Stage, Layer, Image } from "react-konva";
import useImage from "use-image";
import {
  clampStagePosition,
  computeFocusView,
  computeKeyboardZoom,
  computeNormalizedViewport,
  computeVisibleImageRect,
  computeWheelButtonDragZoom,
  mapContainerPointToImage,
  useGetDimensions,
  useSizeListener,
  type AnnotationViewport,
  type KeyboardZoomDirection,
  type StageFocusRequest,
  type StageView,
} from "./helpers";
import { Vector2d } from "konva/lib/types";
import Konva from "konva";
import { KonvaEventObject } from "konva/lib/Node";
import { installKonvaCanvasReadbackHint } from "../konvaCanvasContext";

const SCALE_FACTOR = 1.05;
const ANIMATION_DURATION = 0.2;
/** One keyboard zoom step (a touch punchier than a single wheel tick). */
const KEYBOARD_ZOOM_FACTOR = 1.2;
/** Upper zoom bound for keyboard zoom (the wheel path is unbounded, but key-repeat needs a stop). */
const MAX_KEYBOARD_ZOOM = 8;
/** Wheel-button drag zoom feel: e^(px * this) — ~300px of drag ≈ 4.5× zoom. */
const WHEEL_BUTTON_ZOOM_SENSITIVITY = 0.005;

// Left button only drives the stage's drag-to-pan; Konva's default also lets the middle
// button pan, which would fight the wheel-button press-drag zoom below.
Konva.dragButtons = [0];

installKonvaCanvasReadbackHint();

/** A large animated zoom is a textbook vestibular trigger; honor the OS setting and jump instead. */
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Targets where Space means something else (activating a control, typing) and must not start a pan. */
const SPACE_PAN_IGNORE_SELECTOR =
  "input, textarea, select, button, [contenteditable='true'], [role='alertdialog'], [role='slider']";

/** A live drag wins the cursor, then the Space-pan modifier, then whatever the consumer asked for. */
function resolveStageCursorClass(isDragging: boolean, isSpacePanning: boolean, cursorClassName?: string): string {
  if (isDragging) return "cursor-grabbing";
  if (isSpacePanning) return "cursor-grab";
  return cursorClassName ?? "cursor-auto";
}

interface AnnotationViewerBaseProps extends PropsWithChildren {
  imageUrl: string;
  onDoubleClick?: (point: { x: number; y: number }) => void;
  onImageMouseDown?: (point: { x: number; y: number }) => void;
  onImageMouseMove?: (point: { x: number; y: number }) => void;
  onImageMouseUp?: (point: { x: number; y: number }) => void;
  onImageMouseLeave?: () => void;
  onlyImage?: boolean;
  cursorClassName?: string;
  disableStageDrag?: boolean;
  /**
   * While a press is in progress, keep tracking the pointer via window events and
   * clamp it to the image bounds — so a box drawn past the edge (or with the cursor
   * off the canvas) follows the cursor instead of being dropped. Enable only for the
   * drawing editor (edit mode); hover/select consumers leave it off.
   */
  captureDragOutsideImage?: boolean;
  /**
   * One-shot command to jump the stage: fill the viewport with `bbox`, or animate back to the
   * whole-image fit when it is null. This is NOT a controlled viewport — once a request lands the
   * user is free to zoom and pan away, and nothing pulls them back until `token` changes. Omit the
   * prop entirely (preview mode, the keypoint viewer) and the stage never moves on its own.
   */
  focusRequest?: StageFocusRequest | null;
  onZoomChange?: (totalScale: number) => void;
  onFitScaleChange?: (fitScale: number) => void;
  onViewportChange?: (viewport: AnnotationViewport) => void;
  /** The image-space rectangle currently on screen, emitted on every zoom/pan/resize. */
  onVisibleImageRectChange?: (rect: { x: number; y: number; width: number; height: number }) => void;
}

export const AnnotationViewerBase = ({
  children,
  imageUrl,
  onDoubleClick,
  onImageMouseDown,
  onImageMouseMove,
  onImageMouseUp,
  onImageMouseLeave,
  onlyImage,
  cursorClassName,
  disableStageDrag,
  captureDragOutsideImage,
  focusRequest,
  onZoomChange,
  onFitScaleChange,
  onViewportChange,
  onVisibleImageRectChange,
}: AnnotationViewerBaseProps) => {
  const stageRef = useRef<Konva.Stage>(null);
  const { containerRef, size } = useSizeListener();
  const { width: viewportWidth, height: viewportHeight } = size;
  const [image, imageStatus] = useImage(imageUrl, "anonymous");
  const [currentZoom, setCurrentZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);

  // Hold Space to pan. Edit mode turns off stage drag so left-drag can draw boxes, which leaves a
  // mouse-only reviewer stranded once review-mode focus zooms deep into an object. Space is the
  // familiar image-editor modifier and leaves every existing button binding untouched. The ref
  // mirrors the state so the stage's mousedown can check it without depending on a render.
  const [isSpacePanning, setIsSpacePanning] = useState(false);
  const isSpacePanningRef = useRef(false);
  const setSpacePanning = useCallback((value: boolean) => {
    isSpacePanningRef.current = value;
    setIsSpacePanning(value);
  }, []);

  const {
    scale: initialScale,
    scaledImageWidth,
    scaledImageHeight,
    offsetX,
    offsetY,
  } = useGetDimensions(image, viewportWidth, viewportHeight);

  const emitViewport = useCallback(
    (position: Vector2d, zoom: number) => {
      if (!image || viewportWidth <= 0 || viewportHeight <= 0) return;
      onViewportChange?.(
        computeNormalizedViewport(position, zoom, initialScale, { width: image.width, height: image.height }, {
          width: viewportWidth,
          height: viewportHeight,
        })
      );
      // Emitted alongside the normalized viewport because chrome that must stay
      // READABLE (a hover callout) needs the on-screen rect in image pixels, which
      // the normalized {zoom, center} cannot express.
      onVisibleImageRectChange?.(
        computeVisibleImageRect(position, zoom, initialScale, { width: viewportWidth, height: viewportHeight })
      );
    },
    [image, initialScale, onViewportChange, onVisibleImageRectChange, viewportHeight, viewportWidth]
  );

  const clampPosition = useCallback(
    (position: Vector2d, stageScale: number) =>
      clampStagePosition(
        position,
        stageScale,
        { width: scaledImageWidth, height: scaledImageHeight },
        { width: viewportWidth, height: viewportHeight }
      ),
    [scaledImageWidth, scaledImageHeight, viewportWidth, viewportHeight]
  );

  // Non-null while a focus flight is animating. A user gesture must always win over it, so every
  // gesture entry point cancels first.
  const focusTweenRef = useRef<Konva.Tween | null>(null);

  /** Stop an in-flight focus flight and adopt wherever it got to, so React state matches the stage. */
  const cancelFocusTween = useCallback(() => {
    const tween = focusTweenRef.current;
    if (!tween) return;
    focusTweenRef.current = null;
    tween.destroy();
    const stage = stageRef.current;
    if (!stage) return;
    setCurrentZoom(stage.scaleX());
    onZoomChange?.(stage.scaleX());
  }, [onZoomChange]);

  /**
   * The single place that writes an absolute stage transform. Both the fit-to-container reset and
   * the review-mode box focus go through it, so the two can never drift; `applyFocalZoom` computes
   * its anchored target and delegates here too.
   *
   * Deliberately has NO "same scale, skip it" short-circuit — focusing the next object may well
   * need the same zoom as the last one, and it still has to move.
   */
  const applyStageView = useCallback(
    ({ stageZoom, stagePosition }: StageView, { animate }: { animate: boolean }) => {
      const stage = stageRef.current;
      if (!stage) return;
      // A NaN reaching stage.scale()/position() blanks the canvas with no way to recover.
      if (!Number.isFinite(stageZoom) || !Number.isFinite(stagePosition.x) || !Number.isFinite(stagePosition.y)) return;
      cancelFocusTween();

      // Publish the TARGET zoom immediately rather than at tween end: `currentZoom` gates stage
      // drag, the scroll-to-pan branch and the zoom badge, and every annotation sizes its chrome
      // from it. Waiting would drop a gesture made mid-flight, and re-rendering the whole
      // annotation tree once per tween frame would be far worse. Only the stage transform animates.
      setCurrentZoom(stageZoom);
      onZoomChange?.(stageZoom);

      const settle = () => {
        stage.scale({ x: stageZoom, y: stageZoom });
        stage.position(stagePosition);
        stage.batchDraw();
        emitViewport(stagePosition, stageZoom);
      };

      if (!animate || prefersReducedMotion()) {
        settle();
        return;
      }

      // Konva's `node.to()` returns void, so it offers no cancellation handle — build the tween by
      // hand and keep it so a user gesture can destroy it mid-flight.
      const tween = new Konva.Tween({
        node: stage,
        duration: ANIMATION_DURATION,
        easing: Konva.Easings.EaseInOut,
        x: stagePosition.x,
        y: stagePosition.y,
        scaleX: stageZoom,
        scaleY: stageZoom,
        // Keep the depth-debug mini-map tracking the flight, not just its destination.
        onUpdate: () => emitViewport(stage.position(), stage.scaleX()),
        onFinish: () => {
          if (focusTweenRef.current === tween) focusTweenRef.current = null;
          tween.destroy();
          settle(); // snap off float drift and emit the exact final values
        },
      });
      focusTweenRef.current = tween;
      tween.play();
    },
    [cancelFocusTween, emitViewport, onZoomChange]
  );

  // Apply a focal-point zoom to the stage: pick the scale, reposition so `focal` stays put, and hand
  // the absolute result to applyStageView. Shared by the ctrl/pinch wheel path, the wheel-button
  // press-drag, and the keyboard-zoom shortcuts.
  const applyFocalZoom = useCallback(
    (focal: Vector2d, newScale: number) => {
      const stage = stageRef.current;
      if (!stage) return;
      // Cancel before reading the scale so we anchor on where the flight actually got to.
      cancelFocusTween();
      const currentScale = stage.scaleX();
      if (newScale === currentScale) return;

      applyStageView(
        {
          stageZoom: newScale,
          stagePosition: clampPosition(
            {
              x: focal.x - ((focal.x - stage.x()) / currentScale) * newScale,
              y: focal.y - ((focal.y - stage.y()) / currentScale) * newScale,
            },
            newScale
          ),
        },
        { animate: false }
      );
    },
    [applyStageView, cancelFocusTween, clampPosition]
  );

  const onWheel = useCallback(
    (e: KonvaEventObject<WheelEvent>) => {
      e.evt.preventDefault();

      const stage = stageRef.current;
      if (!stage) return;
      // Wheeling is a user gesture: it wins over an in-flight focus flight.
      cancelFocusTween();

      if (e.evt.ctrlKey) {
        // Trackpad pinch / ctrl+wheel = zoom, anchored on the pointer.
        const pointer = stage.getPointerPosition();
        if (!pointer) return;
        const currentScale = stage.scaleX();
        const newScale = Math.max(1, e.evt.deltaY > 0 ? currentScale / SCALE_FACTOR : currentScale * SCALE_FACTOR);
        applyFocalZoom(pointer, newScale);
      } else {
        // Two-finger trackpad scroll — pan only when zoomed in
        if (currentZoom <= 1) return;
        const newPos = clampPosition({ x: stage.x() - e.evt.deltaX, y: stage.y() - e.evt.deltaY }, currentZoom);
        stage.position({ x: newPos.x, y: newPos.y });
        stage.batchDraw();
        emitViewport(newPos, currentZoom);
      }
    },
    [applyFocalZoom, cancelFocusTween, clampPosition, currentZoom, emitViewport]
  );

  // Keyboard zoom: pick a clamped scale, then zoom about the viewport center (there is no pointer).
  // Reused by the Cmd/Ctrl/Shift +/- shortcuts below.
  const zoomByFactor = useCallback(
    (direction: KeyboardZoomDirection) => {
      const stage = stageRef.current;
      if (!stage) return;
      const newScale = computeKeyboardZoom(stage.scaleX(), direction, KEYBOARD_ZOOM_FACTOR, 1, MAX_KEYBOARD_ZOOM);
      applyFocalZoom({ x: size.width / 2, y: size.height / 2 }, newScale);
    },
    [applyFocalZoom, size.width, size.height]
  );

  const onDblClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      const stage = stageRef.current;
      if (!stage) return;
      const pointer = stage.getPointerPosition();
      const stageScale = stage.scaleX();
      const stagePos = stage.position();

      onDoubleClick?.({
        x: (pointer.x - stagePos.x) / (stageScale * initialScale),
        y: (pointer.y - stagePos.y) / (stageScale * initialScale),
      });
    },
    [onDoubleClick, stageRef, initialScale]
  );

  const getImagePoint = useCallback(() => {
    const stage = stageRef.current;
    if (!stage || !image) return null;
    const pointer = stage.getPointerPosition();
    if (!pointer) return null;
    // getPointerPosition() is already container-relative, so it maps like any
    // container point (no clamp: out-of-bounds returns null, as hover/select expect).
    return mapContainerPointToImage(pointer, stage.position(), stage.scaleX(), initialScale, {
      width: image.width,
      height: image.height,
    });
  }, [image, initialScale]);

  // Non-null while a draw drag is being tracked via window listeners; calling it tears
  // the listeners down. A ref (not state) so the stage handlers can cheaply check it
  // without re-rendering, and so cleanup on unmount can reach the current teardown.
  const endDrawSessionRef = useRef<(() => void) | null>(null);
  const isDrawSessionActive = () => endDrawSessionRef.current !== null;

  // The window listeners below are created once at mousedown but must call the LATEST
  // handlers: Segmentation's onImageMouseUp closes over `draftBox`, which is still null
  // at mousedown, so calling the mousedown-era closure would early-return and the box
  // would never finalize (nor its draft clear). Route through refs kept current each render.
  //
  // `focusRequest` rides along for the same reason, plus one of its own: the parent builds it
  // inline, so its object identity changes on every render. Depending on the object would re-apply
  // the focus on every unrelated re-render and yank the user back to the box; the effect below
  // depends on the primitive `focusToken` and reads the bbox from here.
  const onImageMouseMoveRef = useRef(onImageMouseMove);
  const onImageMouseUpRef = useRef(onImageMouseUp);
  const focusRequestRef = useRef(focusRequest);
  const applyStageViewRef = useRef(applyStageView);
  useEffect(() => {
    onImageMouseMoveRef.current = onImageMouseMove;
    onImageMouseUpRef.current = onImageMouseUp;
    focusRequestRef.current = focusRequest;
    applyStageViewRef.current = applyStageView;
  });

  const startDrawSession = useCallback(() => {
    if (endDrawSessionRef.current) return;
    const stage = stageRef.current;
    if (!stage || !image) return;
    // The container's on-screen rect can't change mid-gesture (the stage isn't
    // pannable/zoomable while drawing), so capture it once rather than re-querying
    // layout on every mousemove. Clamp to the image so a box drawn past the edge — or
    // released with the cursor off the canvas — keeps following the pointer.
    const rect = stage.container().getBoundingClientRect();
    const imageSize = { width: image.width, height: image.height };
    const pointFromEvent = (ev: MouseEvent) =>
      mapContainerPointToImage(
        { x: ev.clientX - rect.left, y: ev.clientY - rect.top },
        stage.position(),
        stage.scaleX(),
        initialScale,
        imageSize,
        { clamp: true }
      );
    const onWindowMove = (ev: MouseEvent) => {
      const point = pointFromEvent(ev);
      if (point) onImageMouseMoveRef.current?.(point);
    };
    const onWindowUp = (ev: MouseEvent) => {
      const point = pointFromEvent(ev);
      if (point) onImageMouseUpRef.current?.(point);
      endDrawSessionRef.current?.();
    };
    window.addEventListener("mousemove", onWindowMove);
    window.addEventListener("mouseup", onWindowUp);
    endDrawSessionRef.current = () => {
      window.removeEventListener("mousemove", onWindowMove);
      window.removeEventListener("mouseup", onWindowUp);
      endDrawSessionRef.current = null;
    };
  }, [image, initialScale]);

  // Wheel-button (middle mouse) press-drag zoom: while held, vertical movement zooms
  // about the press point (like pinch — forward/up zooms in, back/down zooms out).
  // Window listeners keep it live if the cursor leaves the canvas; a ref lets unmount
  // tear it down mid-drag.
  const endWheelZoomRef = useRef<(() => void) | null>(null);
  const startWheelButtonZoom = useCallback(
    (downEvt: MouseEvent) => {
      if (endWheelZoomRef.current) return;
      const stage = stageRef.current;
      if (!stage) return;
      const rect = stage.container().getBoundingClientRect();
      // Anchor on the press point so the content under the wheel-click stays put.
      const focal = { x: downEvt.clientX - rect.left, y: downEvt.clientY - rect.top };
      let lastY = downEvt.clientY;
      const onMove = (ev: MouseEvent) => {
        const s = stageRef.current;
        if (!s) return;
        const deltaUp = lastY - ev.clientY; // forward/up = positive = zoom in
        if (deltaUp === 0) return;
        lastY = ev.clientY;
        applyFocalZoom(focal, computeWheelButtonDragZoom(s.scaleX(), deltaUp, WHEEL_BUTTON_ZOOM_SENSITIVITY, 1));
      };
      const teardown = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", teardown);
        endWheelZoomRef.current = null;
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", teardown);
      endWheelZoomRef.current = teardown;
    },
    [applyFocalZoom]
  );

  useEffect(
    () => () => {
      endDrawSessionRef.current?.();
      endWheelZoomRef.current?.();
      focusTweenRef.current?.destroy();
      focusTweenRef.current = null;
    },
    []
  );

  useEffect(() => {
    const isSpace = (event: KeyboardEvent) => event.code === "Space" || event.key === " ";
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isSpace(event) || event.repeat) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest(SPACE_PAN_IGNORE_SELECTOR)) return;
      event.preventDefault(); // Space would otherwise scroll the page.
      setSpacePanning(true);
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (isSpace(event)) setSpacePanning(false);
    };
    // A tab-switch mid-hold never delivers keyup, which would leave panning stuck on.
    const onWindowBlur = () => setSpacePanning(false);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [setSpacePanning]);

  // Middle (scroll-wheel) button press-drag zoom. Intercept it on the container in the
  // CAPTURE phase, before Konva dispatches the mousedown to any child — a selected box
  // body, resize corner, or editable measurement endpoint sets cancelBubble on mousedown,
  // which would otherwise swallow the press and make wheel-drag zoom a no-op over those
  // surfaces. stopPropagation keeps Konva from also starting a draw/pan for this button.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const onMouseDownCapture = (e: MouseEvent) => {
      if (e.button !== 1) return;
      e.preventDefault();
      e.stopPropagation();
      startWheelButtonZoom(e);
    };
    container.addEventListener("mousedown", onMouseDownCapture, { capture: true });
    return () => container.removeEventListener("mousedown", onMouseDownCapture, { capture: true });
  }, [containerRef, startWheelButtonZoom]);

  const onStageMouseDown = useCallback(
    () => {
      // Space+drag is a pan, not a draw — Konva fires mousedown AND starts the stage drag, so
      // without this a pan would also open a draft box.
      if (isSpacePanningRef.current) return;
      const point = getImagePoint();
      if (!point) return;
      onImageMouseDown?.(point);
      // Hand the rest of this press to window-level tracking so a box can be drawn past
      // the image edge and finished even if the cursor leaves the canvas entirely.
      if (captureDragOutsideImage) startDrawSession();
    },
    [captureDragOutsideImage, getImagePoint, onImageMouseDown, startDrawSession]
  );

  const onStageMouseMove = useCallback(() => {
    // The window listener owns pointer tracking during a draw drag — the stage's own
    // move would fire onImageMouseLeave off-image and cancel the in-progress box.
    if (isDrawSessionActive()) return;
    const point = getImagePoint();
    if (!point) {
      onImageMouseLeave?.();
      return;
    }
    onImageMouseMove?.(point);
  }, [getImagePoint, onImageMouseLeave, onImageMouseMove]);

  const onStageMouseUp = useCallback(() => {
    // The window listener already delivered the final point for this drag.
    if (isDrawSessionActive()) return;
    const point = getImagePoint();
    if (!point) return;
    onImageMouseUp?.(point);
  }, [getImagePoint, onImageMouseUp]);

  const onStageMouseLeave = useCallback(() => {
    // Don't cancel an in-progress draw when the cursor leaves the canvas — the window
    // listener keeps the box following the pointer until release.
    if (isDrawSessionActive()) return;
    onImageMouseLeave?.();
  }, [onImageMouseLeave]);

  const onDragStart = useCallback(() => {
    cancelFocusTween();
    setIsDragging(true);
  }, [cancelFocusTween]);

  const onDragMove = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    emitViewport(stage.position(), stage.scaleX());
  }, [emitViewport]);

  const onDragEnd = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const stageScale = stage.scaleX();
    const newPos = clampPosition(stage.position(), stageScale);
    stage.to({ x: newPos.x, y: newPos.y, duration: ANIMATION_DURATION });
    stage.batchDraw();
    emitViewport(newPos, stageScale);
    setIsDragging(false);
  }, [stageRef, clampPosition, emitViewport]);

  // Keyboard zoom while the viewer is open: Cmd/Ctrl +/- and Shift +/- zoom the image and preempt the
  // browser's page zoom. Mount-scoped — the browser default is restored the moment the viewer closes.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey || event.shiftKey)) return;
      const target = event.target as HTMLElement | null;
      // Ignore typing targets and keys pressed inside a modal prompt over the viewer.
      // Only [role='alertdialog'] — the viewer lightbox itself is a [role='dialog'].
      if (target?.closest("input, textarea, select, [contenteditable='true'], [role='alertdialog']")) return;
      const zoomIn = event.key === "+" || event.key === "=";
      const zoomOut = event.key === "-" || event.key === "_";
      if (!zoomIn && !zoomOut) return;
      event.preventDefault();
      zoomByFactor(zoomIn ? "in" : "out");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [zoomByFactor]);

  /** The whole-image view: stage zoom 1, letterboxed centre. Also what a whole-image focus returns. */
  const fitView = useMemo<StageView>(
    () => ({ stageZoom: 1, stagePosition: { x: offsetX, y: offsetY } }),
    [offsetX, offsetY]
  );

  // Reset to the centered whole-image view whenever the image or container size changes.
  useEffect(() => {
    if (!scaledImageWidth || !scaledImageHeight) return;
    applyStageView(fitView, { animate: false });
    onFitScaleChange?.(initialScale);
  }, [applyStageView, fitView, initialScale, onFitScaleChange, scaledImageHeight, scaledImageWidth]);

  // Apply a focus request. Declared AFTER the reset effect on purpose: React runs effects in
  // declaration order, so on a geometry commit the reset fits the whole image first and this
  // overwrites it — both batchDraw() calls coalesce into a single paint, so there is no flash.
  const focusToken = focusRequest?.token ?? null;
  const lastAppliedFocusTokenRef = useRef<number | null>(null);
  useEffect(() => {
    const request = focusRequestRef.current;
    if (!request || focusToken === null) return;
    // Geometry isn't real yet — on a fresh mount the ResizeObserver hasn't fired and useImage
    // hasn't resolved, so initialScale is a placeholder 1 and scaledImageWidth is 0. Bail; this
    // effect re-runs the moment real geometry arrives, which is how a focus requested before the
    // image loads survives without any explicit queue. That race is the norm, not the exception:
    // the viewer remounts for every image.
    if (!scaledImageWidth || !scaledImageHeight || viewportWidth <= 0 || viewportHeight <= 0) return;

    const view = request.bbox
      ? computeFocusView(
          request.bbox,
          initialScale,
          { width: scaledImageWidth, height: scaledImageHeight },
          { width: viewportWidth, height: viewportHeight }
        )
      : fitView;
    if (!view) return;

    // Animate only a genuinely new request. A geometry-driven re-application (mount, image load,
    // container resize) lands instantly — the reset effect just snapped to fit in this same
    // commit, so there is nothing meaningful to animate from.
    const animate = lastAppliedFocusTokenRef.current !== focusToken;
    lastAppliedFocusTokenRef.current = focusToken;
    applyStageViewRef.current(view, { animate });
  }, [focusToken, fitView, initialScale, scaledImageWidth, scaledImageHeight, viewportWidth, viewportHeight]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative">
      <Stage
        className={resolveStageCursorClass(isDragging, isSpacePanning, cursorClassName)}
        width={size.width || 1}
        height={size.height || 1}
        ref={stageRef}
        style={{ position: "absolute", top: 0, left: 0 }}
        onWheel={onWheel}
        onDblClick={onDblClick}
        onMouseDown={onStageMouseDown}
        onMouseMove={onStageMouseMove}
        onMouseUp={onStageMouseUp}
        onMouseLeave={onStageMouseLeave}
        // Holding Space overrides the editor's drag-to-draw so the reviewer can pan while zoomed in.
        draggable={currentZoom > 1 && (!disableStageDrag || isSpacePanning)}
        onDragStart={onDragStart}
        onDragMove={onDragMove}
        onDragEnd={onDragEnd}>
        <Layer
          scaleX={initialScale}
          scaleY={initialScale}>
          {initialScale && (
            <>
              <Image
                image={image}
                listening={false}
              />
              {imageStatus !== "loading" && !onlyImage && children}
            </>
          )}
        </Layer>
      </Stage>

      {currentZoom > 1 && (
        <div className="absolute bottom-4 left-4 px-2 py-1 bg-black/50 text-white text-xs rounded">
          {Math.round(currentZoom * 100)}%
        </div>
      )}
    </div>
  );
};
